import React from "react";
import { Row, Col } from "reactstrap";
import { Helmet } from "react-helmet";
import Footer from '../../../components/Footer';
import { ArticleContent } from "../../../style/Blog";
import CardClotereSidebarQuestion from "../../CardClotereSidebarQuestion";
import HeaderPost from '../../HeaderPost';
import ArticlesSimilaires from "../../ArticlesSimilaires";
import PrendreRdv from "../../../leadPage/prendreRdv";
import FaqVendeur from "../../FaqVendeur";
import PlusLoinVendeur from "../../components/PlusLoinVendeur";
import { Link } from 'react-router-dom';

function ArticleCompromisVente() {
  return (
    <>
      <Helmet>
        <title>Tout comprendre sur le compromis de vente immobilière chez le notaire | Clotere</title>
        <meta name="description" content="Quels documents prévoir pour vendre un bien immobilier ? Liste complète des pièces à fournir par le vendeur et l'acheteur pour le compromis de vente avec Clotere." />
        <meta property="og:url" content="https://clotere.fr/blog/documents-vente-immobiliere" />
        <meta property="og:title" content="Documents à fournir pour une vente immobilière" />
        <meta property="og:description" content="Clotere vous accompagne dans votre vente immobilière en vous indiquant tous les documents nécessaires à fournir au notaire pour le compromis." />
        <meta property="og:image" content="https://clotere.fr/images/blog-vente-immo.webp" />
      </Helmet>

      <HeaderPost titre="Tout comprendre sur le compromis de vente immobilière chez le notaire" breadlink1="/blog" namelink1="Blog" namelinkActive="Documents pour la vente immobilière" />

      <ArticleContent className="bg-gradient-to-b from-transparent to-purple-50 pb-40">
        <section className="content">
          <div className="container">
            <Row>
              <Col className="col-sm-9 col-xs-12 article">
                <h2>📌 Introduction</h2>
                <p>L’achat ou la vente d’un bien immobilier passe par plusieurs étapes essentielles, dont la signature d’un compromis de vente. Ce document engage juridiquement les deux parties et fixe les conditions de la vente avant l’acte définitif.</p>
                <p>Mais qu’est-ce qu’un compromis de vente exactement ? Quelle différence avec une promesse de vente ? Quels documents sont requis ? Qui rédige le compromis et qui en assume les frais ? Autant de questions auxquelles nous allons répondre dans cet article.</p>

                <h2>🏡 Qu’est-ce qu’un compromis de vente ?</h2>
                <p>Un compromis de vente est un avant-contrat signé entre un acheteur et un vendeur, dans lequel ils s’engagent réciproquement à conclure la transaction immobilière. Il définit les conditions essentielles de la vente, comme le prix, les délais et les éventuelles conditions suspensives (obtention d’un prêt, par exemple).</p>
                <h3> Pourquoi est-il important ?</h3>
                <ul>
                  <li>Il sécurise la transaction en fixant des règles claires.</li>
                  <li>Il permet aux deux parties de se préparer à la vente définitive.</li>
                  <li>Il donne un délai pour obtenir un crédit immobilier si nécessaire.</li>
                </ul>
                <p>Le compromis de vente est donc une étape incontournable avant la signature de l’acte authentique chez le notaire.</p>

                <h2>⚖️ Promesse de vente : quelle différence avec le compromis de vente ?</h2>
                <p>Le compromis de vente est souvent confondu avec la promesse de vente, mais ces deux documents ne produisent pas les mêmes effets juridiques.</p>
                <h3> Compromis de vente</h3>
                <ul>
                  <li>Engage les deux parties de manière réciproque.</li>
                  <li>Si l’une des parties renonce sans raison valable, elle peut être contrainte d’exécuter la vente ou de verser des dommages et intérêts.</li>
                  <li>En général, l’acheteur verse un dépôt de garantie de 5 à 10 % du prix du bien.</li>
                </ul>
                <h3> Promesse unilatérale de vente</h3>
                <ul>
                  <li>N’engage que le vendeur, qui s’engage à vendre à l’acheteur s’il le souhaite.</li>
                  <li>L’acheteur peut décider de lever ou non l’option d’achat dans un délai défini (souvent 2 à 3 mois).</li>
                  <li>L’acheteur verse une indemnité d’immobilisation (généralement 5 à 10 % du prix du bien).</li>
                </ul>
                <blockquote className="blog-quote"><strong>🔎 En résumé :</strong> Le compromis de vente est un engagement ferme des deux parties, tandis que la promesse unilatérale laisse une plus grande liberté à l’acheteur.</blockquote>

                <h2>📋 Quels sont les documents nécessaires pour signer un compromis de vente ?</h2>
                <br />
                <h3> Documents fournis par le vendeur</h3>
                <ul>
                  <li>Titre de propriété du bien</li>
                  <li>Diagnostics immobiliers obligatoires (DPE, amiante, plomb, termites, etc.)</li>
                  <li>Derniers avis de taxe foncière et d’habitation</li>
                  <li>Règlement de copropriété (si le bien est en copropriété)</li>
                  <li>Procès-verbaux des assemblées générales des 3 dernières années</li>
                </ul>
                <h3> Documents demandés à l’acheteur</h3>
                <ul>
                  <li>Pièce d’identité et justificatif de domicile</li>
                  <li>Offre de prêt immobilier (si applicable)</li>
                  <li>Éventuelle attestation de financement</li>
                </ul>
                <blockquote className="blog-quote"><strong>🔎 En résumé :</strong> Un dossier complet est indispensable pour éviter les litiges et assurer la transparence de la vente.</blockquote>

                <h2>✍️ Qui rédige le compromis de vente ?</h2>
                <p>Plusieurs possibilités pour la rédaction du compromis de vente :</p>
                <br /><h3> Chez le notaire</h3>
                <ul>
                  <li>Assure une sécurité juridique maximale</li>
                  <li>Vérifie la conformité des documents et des conditions de vente</li>
                  <li>Recommandé pour les ventes complexes ou les biens en copropriété</li>
                </ul>
                <h3> Par une agence immobilière</h3>
                <ul>
                  <li>Solution courante lorsqu’un agent immobilier est mandaté</li>
                  <li>Utilise des modèles standardisés mais juridiquement valables</li>
                  <li>Moins sécurisé qu’un acte notarié, mais plus rapide</li>
                </ul>
                <h3> Entre particuliers</h3>
                <ul>
                  <li>Possible, mais risqué en l’absence de connaissance juridique</li>
                  <li>Doit être obligatoirement enregistré chez un notaire pour être valable</li>
                </ul>
                <blockquote className="blog-quote"><strong>🔎 En résumé :</strong> Il est conseillé de passer par un notaire pour garantir la validité et la conformité du compromis de vente.</blockquote>

                <h2>⏳ Quel est le délai de rétractation après la signature d’un compromis de vente ?</h2>
                <p>L’acheteur bénéficie d’un délai de rétractation de 10 jours après la signature du compromis.</p>
                <h3> Comment fonctionne ce délai ?</h3>
                <ul>
                  <li>Il commence le lendemain de la remise du compromis signé</li>
                  <li>L’acheteur peut se rétracter sans justification et récupérer son dépôt de garantie</li>
                  <li>Passé ce délai, l’engagement devient ferme et définitif</li>
                </ul>
                <h3> Comment notifier la rétractation ?</h3>
                <ul>
                  <li>Par lettre recommandée avec accusé de réception au vendeur ou au notaire</li>
                  <li>La rétractation est sans pénalité, et l’acheteur récupère la totalité des sommes avancées</li>
                </ul>

                <h2>💰 Qui doit payer le compromis de vente chez le notaire ?</h2>
                <br />
                <h3> Les frais de rédaction</h3>
                <ul>
                  <li>Généralement pris en charge par l’acheteur, sauf accord contraire entre les parties</li>
                  <li>Peuvent varier entre 200 et 500 euros, selon la complexité du dossier</li>
                </ul>
                <h3> Le dépôt de garantie</h3>
                <ul>
                  <li>Versé par l’acheteur au moment de la signature (5 à 10 % du prix du bien)</li>
                  <li>Déduit du prix total lors de la signature de l’acte définitif</li>
                </ul>
                <h3> Les frais de notaire définitifs</h3>
                <ul>
                  <li>Payés uniquement lors de la signature de l’acte authentique (environ 7 à 8 % du prix du bien pour un logement ancien)</li>
                </ul>

                <h2>Conclusion</h2>
                <p>Le compromis de vente est une étape clé dans un achat immobilier. Il engage juridiquement le vendeur et l’acheteur, sécurise la transaction et permet d’organiser la vente définitive en toute sérénité.</p>
                <h3>👋 A retenir au sujet du compromis de vente :</h3>
                <ul>
                  <li>Il formalise l’accord entre acheteur et vendeur avant la vente définitive</li>
                  <li>Il diffère de la promesse unilatérale, qui n’engage pas autant l’acheteur</li>
                  <li>Il doit être accompagné de nombreux documents justificatifs</li>
                  <li>Il peut être rédigé par un notaire, une agence ou entre particuliers</li>
                  <li>L’acheteur dispose d’un délai de rétractation de 10 jours après signature</li>
                  <li>Les frais de rédaction sont généralement à la charge de l’acheteur</li>
                </ul>
                <h2>Votre compromis de vente avec Clotere</h2>
                <br/>
                <img src="https://framerusercontent.com/images/EeTxMuJjm8J91QN2AaixVRAktU.png" alt="Compromis de vente notaire - Clotere"/>

                  <blockquote className="blog-quote">Avant votre offre d'achat, choisissez votre notaire et constituez votre dossier simplement depuis votre espace sécurisé Clotere.
                    <Link to="/onboard/vente" className="btn-main text-white no-underline">Débutez gratuitement votre dossier de vente</Link>
                  </blockquote>
                
                <PlusLoinVendeur />
              </Col>

              <div className="col-sm-3 col-xs-0 sidebar hidden sm:block">
                <CardClotereSidebarQuestion />
              </div>

              <div className="col-xs-0 sm:hidden">
                <CardClotereSidebarQuestion />
              </div>
            </Row>
          </div>
        </section>
      </ArticleContent>
      <FaqVendeur />
      <ArticlesSimilaires title="Articles similaires" type="immobilier" />
      <PrendreRdv />
      <Footer />
    </>
  );

}

export default ArticleCompromisVente;